import type { BaseSyntheticEvent } from 'react'
import type { CancelTokenSource } from 'axios'
import { Form } from '@epages/react-components'
import { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import cc from 'classcat'

import { ImageUploadControl } from '../../../../UnsplashImageSearch/components/ImageUploadControl'
import { allowedMimeTypes } from '../../daliConfig'
import { useTranslate } from '../../../../../utils/translate'
import Button from '../../../../formComponents/Button'
import ButtonRow from '../../../../formComponents/ButtonRow'
import CheckboxField from '../../../../formComponents/CheckboxField'
import FormGroup from '../../../../formComponents/FormGroup'
import FormValue from '../../../../formComponents/FormValue'
import InputField from '../../../../formComponents/InputField'
import LinkTooltipSettingsLayer from '../components/LinkTooltip/LinkTooltipSettingsLayer'

type Props = {
  data: ImmutableMap
  selectedImagePosition: number
  onApply: (image: ImmutableMap) => void
  onCancel: () => void
  imageUrl: (src: string) => string
  previewUrl: (src: string) => string
  storeFile: (file: File, config?: any) => Promise<Storage.Image>
  helpCenterUrl: string
}

export default function ImageEditPopover({
  data,
  selectedImagePosition,
  onApply,
  onCancel,
  imageUrl,
  previewUrl,
  storeFile,
  helpCenterUrl,
}: Readonly<Props>) {
  const t = useTranslate('interface', 'components.imageGalleryComponent.imageEditModal')

  const [formData, setFormData] = useState<ImmutableMap>(
    data.get('images').find((img: ImmutableMap) => img.get('position') === selectedImagePosition),
  )
  const [cancelToken, setCancelToken] = useState<CancelTokenSource | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)

  const imageSrc = cancelToken ? formData.get('localPreview') : previewUrl(imageUrl(formData.get('src')))
  const isLinkEmpty = !formData.get('link')

  const [errorStatus, setErrorStatus] = useState<
    number | 'gifHasTooManyFrames' | 'imageResolutionTooBig' | 'unknown' | null
  >(null)

  useEffect(() => {
    return () => {
      cancelToken?.cancel()
    }
  }, [cancelToken])

  const handleChange = (value: ImmutableMap) => {
    setFormData(value)
  }

  const processImageFile = async (file: File) => {
    const cancelTokenSource = axios.CancelToken.source()
    const config = { cancelToken: cancelTokenSource.token }

    setFormData(formData.update('localPreview', () => URL.createObjectURL(file)))
    setCancelToken(cancelTokenSource)

    const response = await handleUpload(file, config)

    if (response) {
      setFormData((formData) =>
        formData.merge({
          src: response.absoluteDownloadUrl,
          width: response.width,
          height: response.height,
        }),
      )
    }
  }

  const handleImageChange = async (event: BaseSyntheticEvent) => {
    if (!event.target.files) return false
    await processImageFile(event.target.files[0])
  }

  const handleUpload = async (file: File, config?: any) => {
    try {
      setErrorStatus(null)
      return await storeFile(file, config)
    } catch (error) {
      if (error?.response?.data?.message?.startsWith('Animated image has too many frames.')) {
        setErrorStatus('gifHasTooManyFrames')
        return null
      }
      if (error?.response?.data?.message?.startsWith('Uploaded image resolution across all frames is too big.')) {
        setErrorStatus('imageResolutionTooBig')
        return null
      }
      setErrorStatus(error?.response?.status || 'unknown')
      return null
    } finally {
      setCancelToken(null)
    }
  }

  const handleApplyButtonClick = () => onApply(formData)

  const handleCancelButtonClick = () => onCancel()

  const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key === 'Enter') {
      onApply(formData)
    }
  }

  const renderSpinner = () => (
    <div className="image-busy-indicator ep-form-row">
      <span className="image-spinner" />
      <img src={imageSrc} draggable="false" alt="" />
    </div>
  )

  const renderImage = () => (
    <div className="image-preview ep-form-row">
      <img src={imageSrc} alt="" />
      <div className="image-button-wrapper">
        <ImageUploadControl
          onUnsplashSelect={(file) => processImageFile(file)}
          onUploadClick={() => fileInput.current?.click()}
          buttonTooltip={t('changeImageButton.tooltip')}
          buttonAriaLabel={t('changeImageButton.accessibilityLabel')}
          buttonClassName="dali-grid-element-edit-button dali-plugin-image-button-change"
          side="top"
        />
      </div>
    </div>
  )

  // Handle the same error statuses than in `ImageFieldMultiple`
  const statusToErrorCode = {
    413: 'requestEntityTooLarge',
    415: 'unsupportedMediaType',
    gifHasTooManyFrames: 'gifHasTooManyFrames',
    imageResolutionTooBig: 'imageResolutionTooBig',
  }

  const getErrorMessage = (): string => {
    if (!errorStatus) return ''

    return t(`:components.imageGalleryComponent.imageFieldMultiple.errorMessages.imageEditModalMessage`, {
      reason: t(
        `:components.imageGalleryComponent.imageFieldMultiple.errorMessages.reason.${statusToErrorCode[errorStatus] || 'unknown'}`,
      ),
    })
  }

  return (
    <>
      {cancelToken ? renderSpinner() : renderImage()}
      {errorStatus && (
        <div className="ep-form-row">
          <div className="dali-notification-danger">
            {getErrorMessage()}
            {errorStatus !== 'gifHasTooManyFrames' && errorStatus !== 'imageResolutionTooBig' && (
              <a
                href={helpCenterUrl}
                target="_blank"
                className="ep-form-row-text-external-link"
                rel="noreferrer noopener"
              >
                {t(':components.imageGalleryComponent.imageFieldMultiple.errorMessages.helpCenterLink')}
              </a>
            )}
          </div>
        </div>
      )}
      <Form name="imageEditPopover" value={formData} onChange={handleChange} onKeyDown={handleKeyDown}>
        <FormGroup
          info={t('alternativeTextInputField.explanation')}
          name="alt"
          tagName="label"
          label={t('alternativeTextInputField.label')}
        >
          <InputField name="alt" type="text" />
        </FormGroup>
        <FormValue name="link">
          {(link, setLink) => (
            <LinkTooltipSettingsLayer t={t} link={link} setLink={setLink} label={'linkInputField.label'} />
          )}
        </FormValue>
        <FormGroup
          className={cc(['ep-form-row-min-margin', { disabled: isLinkEmpty }])}
          name="opentab"
          tagName="label"
          label={t('openLinkInNewTabCheckbox.label')}
          labelAfterComponent
        >
          <CheckboxField name="opentab" disabled={isLinkEmpty} />
        </FormGroup>
        <ButtonRow>
          <Button type="button" variant="cancel" onClick={handleCancelButtonClick}>
            {t('cancelButton.label')}
          </Button>
          <Button type="button" onClick={handleApplyButtonClick}>
            {t('applyButton.label')}
          </Button>
        </ButtonRow>
      </Form>

      <input
        ref={fileInput}
        hidden
        type="file"
        accept={allowedMimeTypes.join(',')}
        onChange={handleImageChange}
        value=""
        data-testid="uploadField"
      />
    </>
  )
}
